import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Lock, ChevronDown, ShieldCheck, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

function PermGroup({ g, perms, onToggle, disabled, roleKey }) {
  const keys = g.items.map((i) => i.key);
  const on = keys.filter((k) => perms.includes(k)).length;
  const all = on === keys.length;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`perm-group-${roleKey}-${g.group}`}>
      <label className="flex items-center gap-2 font-semibold text-slate-800 text-sm cursor-pointer">
        <Checkbox checked={all} className={!all && on ? "bg-sky-200 border-sky-500" : ""} disabled={disabled} onCheckedChange={() => onToggle(keys, !all)} data-testid={`perm-menu-${roleKey}-${g.group}`} />
        {g.label} <span className="ml-auto text-[10px] font-mono text-slate-400">{on}/{keys.length}</span>
      </label>
      <div className="mt-2 pl-6 space-y-1.5">
        {g.items.map((p) => (
          <label key={p.key} className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
            <Checkbox className="mt-0.5" checked={perms.includes(p.key)} disabled={disabled} onCheckedChange={(v) => onToggle([p.key], !!v)} data-testid={`perm-${roleKey}-${p.key}`} />{p.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function RoleCard({ role, catalog, isOwner, onSaved, onCopy }) {
  const [d, setD] = useState({ name: role.name, description: role.description || "", permissions: role.permissions });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setD({ name: role.name, description: role.description || "", permissions: role.permissions }); }, [role]);
  const locked = role.locked || !isOwner;
  const dirty = JSON.stringify(d) !== JSON.stringify({ name: role.name, description: role.description || "", permissions: role.permissions });
  const toggle = (keys, v) => setD((p) => ({ ...p, permissions: v ? [...new Set([...p.permissions, ...keys])] : p.permissions.filter((k) => !keys.includes(k)) }));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/roles/${role.key}`, d); toast.success(`Hak akses ${d.name} disimpan — berlaku langsung`); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Hapus role ${role.name}?`)) return;
    try { await api.delete(`/roles/${role.key}`); toast.success("Role dihapus"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-aqua overflow-hidden" data-testid={`role-card-${role.key}`}>
      <div className="flex items-center">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-left hover:bg-sky-50/60 transition-colors" data-testid={`role-toggle-${role.key}`}>
        <span className={`h-9 w-9 rounded-full flex items-center justify-center ${role.key === "owner" ? "bg-amber-500 text-white" : "bg-slate-900 text-sky-300"}`}><ShieldCheck className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-800 flex items-center gap-2">{role.name}{role.locked && <Lock className="h-3.5 w-3.5 text-slate-400" />}{role.system && !role.locked && <span className="text-[10px] font-semibold text-slate-400 border border-slate-200 rounded px-1">bawaan</span>}</div>
          <div className="text-xs text-slate-500 truncate">{role.description || "—"} • {role.user_count} pengguna • {role.permissions.length} hak akses</div>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {isOwner && <Button type="button" variant="outline" size="sm" className="mr-3 rounded-full border-sky-200 text-sky-700 hover:bg-sky-50 shrink-0" onClick={() => onCopy(role)} title="Buat role baru dari hak akses role ini" data-testid={`btn-copy-role-${role.key}`}><Copy className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Salin</span></Button>}
      </div>
      {open && (
        <div className="border-t border-sky-50 p-4 bg-slate-50/50 space-y-3">
          {!role.locked && isOwner && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nama role</Label><Input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className="bg-white" data-testid={`input-role-name-${role.key}`} /></div>
              <div className="space-y-1.5"><Label>Keterangan</Label><Input value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} className="bg-white" data-testid={`input-role-desc-${role.key}`} /></div>
            </div>
          )}
          {role.locked && <p className="text-xs text-slate-500">Pemilik selalu memiliki seluruh hak akses dan tidak dapat diubah.</p>}
          {!isOwner && !role.locked && <p className="text-xs text-slate-500 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Hanya Pemilik yang dapat mengubah hak akses role.</p>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {catalog.map((g) => <PermGroup key={g.group} g={g} perms={d.permissions} onToggle={toggle} disabled={locked} roleKey={role.key} />)}
          </div>
          {!locked && (
            <div className="flex gap-2 justify-end">
              {!role.system && <Button variant="outline" className="rounded-full text-rose-600 border-rose-200" onClick={remove} data-testid={`btn-delete-role-${role.key}`}><Trash2 className="h-4 w-4 mr-1" /> Hapus Role</Button>}
              <Button className="rounded-full bg-sky-600 hover:bg-sky-700" disabled={!dirty || saving || d.name.trim().length < 2} onClick={save} data-testid={`btn-save-role-${role.key}`}><Save className="h-4 w-4 mr-1" />{saving ? "Menyimpan..." : "Simpan Hak Akses"}</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function RolesManager({ isOwner, roles, onChanged }) {
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/permissions").then((r) => setCatalog(r.data)); }, []);
  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { source, ...body } = form;
    try { await api.post("/roles", body); toast.success(source ? `Role ${body.name} dibuat dengan ${body.permissions.length} hak akses dari ${source}` : "Role dibuat — atur hak aksesnya di bawah"); setForm(null); onChanged(); } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <div className="mt-8" data-testid="roles-manager">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div><h2 className="text-base md:text-lg font-bold text-slate-900">Role & Hak Akses</h2><p className="text-sm text-slate-500">Atur apa saja yang boleh dilakukan setiap role. Centang menu untuk memilih semua tindakan di dalamnya.</p></div>
        {isOwner && <Button className="rounded-full bg-sky-600 hover:bg-sky-700 shrink-0" onClick={() => setForm({ name: "", description: "", permissions: [] })} data-testid="btn-add-role"><Plus className="h-4 w-4 mr-1" /> Tambah Role</Button>}
      </div>
      <div className="space-y-3">{roles.map((r) => <RoleCard key={r.key} role={r} catalog={catalog} isOwner={isOwner} onSaved={onChanged}
        onCopy={(src) => setForm({ name: `Salinan ${src.name}`.slice(0, 40), description: src.description || "", permissions: [...src.permissions], source: src.name })} />)}</div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="role-form-dialog">
          <DialogHeader><DialogTitle>{form?.source ? "Salin Role" : "Tambah Role"}</DialogTitle><DialogDescription data-testid="role-form-desc">{form?.source ? `Role baru akan memiliki ${form.permissions.length} hak akses yang sama dengan ${form.source}. Anda bisa mengubahnya setelah dibuat.` : "Role baru dibuat tanpa hak akses. Setelah dibuat, centang hak aksesnya."}</DialogDescription></DialogHeader>
          {form && (
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-1.5"><Label>Nama role</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} maxLength={40} placeholder="cth. Kasir, Supervisor Gudang" data-testid="input-new-role-name" /></div>
              <div className="space-y-1.5"><Label>Keterangan</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Opsional" data-testid="input-new-role-desc" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForm(null)} data-testid="btn-role-cancel">Batal</Button>
                <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-role-submit">{saving ? "Menyimpan..." : form.source ? "Buat Salinan" : "Buat Role"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
